import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ConfigContext from '../../contexts/ConfigContext';

// Components
import Meta from '../shared/Meta';
import Hero from './content/Hero';
import Triple from './content/Triple';
import CtaImage from './content/CtaImage';
import LogoList from './content/LogoList';
import Testimonials from './content/Testimonials';
import SupportFaq from './faqs/SupportFaq';

// Styles
import styles from '../../../stylesheets/public/layouts/AboutPage.module.postcss';
import supportStyles from '../../../stylesheets/public/layouts/SupportPage.module.postcss';
import tripleStyles from './content/Triple.module.postcss';

function Support() {
  const { support_email, city_name, instagram, twitter, facebook } = useContext(ConfigContext);

  return (
    <div>
      <Meta
        title='Contact & Support'
        description={`Get in touch with SeattleFoodTruck.com. Book food truck catering for events, join as a vendor, or schedule food trucks for your property in ${city_name}.`}
        canonical='/support'
        structuredData={{
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Contact & Support', item: '/support' }
            ]
          }
        }}
      />

      {/* 1. Hero */}
      <Hero photo='https://static.seattlefoodtruck.com/photos/home-hero.webp'>
        <h1 className='u-phosphate'>How Can We Help?</h1>
        <p>Whether you&apos;re planning an event, managing a property, or running a food truck — we&apos;re here for you.</p>
      </Hero>

      {/* 2. Audience Segment Cards */}
      <section className='Section--extraPadding'>
        <h2 className='u-phosphate u-textCenter u-mb2'>What Brings You Here?</h2>
        <div className={`${tripleStyles.Triple_wrapper} ${tripleStyles.Triple_wrapper__quad}`}>
          <Triple
            image='https://static.seattlefoodtruck.com/photos/office2.webp'
            title='Planning an Event?'
            text='Book a food truck for your corporate event, wedding, birthday party, or community gathering. Get free quotes from top local trucks.'
            link='/catering'
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/vendors1.webp'
            title='Own a Food Truck?'
            text='Join our platform to reach thousands of customers, receive catering requests, and grow your business.'
            link='/vendor/register'
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/office.webp'
            title='Manage a Property?'
            text='Bring food trucks to your office park, apartment community, or brewery on a regular schedule.'
            link='/services'
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/community.webp'
            title='Press & Partnerships'
            text={`Media inquiries, sponsorships, and collaborations — email us at ${support_email}.`}
          />
        </div>
      </section>

      {/* 3. Direct Contact Info Bar */}
      <div className={supportStyles.ContactBar}>
        <div className={supportStyles.ContactBar_item}>
          <h4>Call or Text</h4>
          <a href='tel:2067990095'>(206) 799-0095</a>
        </div>
        <div className={supportStyles.ContactBar_item}>
          <h4>Hours</h4>
          <p>Monday – Friday, 9am – 5pm PST</p>
        </div>
        <div className={supportStyles.ContactBar_item}>
          <h4>Email</h4>
          <a href={`mailto:${support_email}`}>{support_email}</a>
        </div>
      </div>

      {/* 4. Logo List */}
      <section className='Section'>
        <LogoList title={`Trusted by leading organizations across ${city_name}`} />
      </section>

      {/* 5. Testimonials */}
      <Testimonials title='What Our Customers Say' />

      {/* 6. FAQ */}
      <section className='Section--extraPadding'>
        <SupportFaq />
      </section>

      {/* 7. Office / Team Info */}
      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/bookings.webp' />
          <div className={styles.Cta_copy}>
            <h3>Our Team</h3>
            <p>
              SeattleFoodTruck.com connects event planners, property managers, and food lovers with the best food trucks in the Pacific Northwest.
            </p>
            <p>
              <strong>Address</strong><br />
              113 Cherry St #45647<br />
              Seattle, WA 98104
            </p>
            <p>
              <strong>Hours</strong><br />
              Monday – Friday, 9am – 5pm PST
            </p>
            <div className={supportStyles.SocialLinks}>
              <a href={facebook} target='_blank' rel='noopener noreferrer' aria-label='Facebook'>
                <img src='/static/images/icons/footer/facebook.svg' alt='Facebook' width='32' height='32' />
              </a>
              <a href={`https://instagram.com/${instagram}`} target='_blank' rel='noopener noreferrer' aria-label='Instagram'>
                <img src='/static/images/icons/footer/instagram.svg' alt='Instagram' width='32' height='32' />
              </a>
              <a href={`https://twitter.com/${twitter}`} target='_blank' rel='noopener noreferrer' aria-label='Twitter'>
                <img src='/static/images/icons/footer/twitter.svg' alt='Twitter' width='32' height='32' />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Bottom CTA */}
      <div className={styles.BookingCta}>
        <h3 className='u-phosphate'>Ready to Get Started?</h3>
        <p>Book a food truck for your next event and get free quotes from top local trucks.</p>
        <Link to='/catering' className='Button'>Start Your Catering Request</Link>
      </div>
    </div>
  );
}

export default Support;
